import {
  SET_STEP,
  SET_FILE,
  DISCOVER_PROGRAMS_START,
  DISCOVER_PROGRAMS_FULFILLED,
  DISCOVER_PROGRAMS_REJECTED,
  SET_SELECTED_PROGRAM,
  EXTRACT_PROGRAM_START,
  EXTRACT_PROGRAM_PROCESSING,
  EXTRACT_PROGRAM_SLOW,
  EXTRACT_PROGRAM_FULFILLED,
  EXTRACT_PROGRAM_REJECTED,
  RESET_IMPORT,
  IMPORT_STEPS,
  UPDATE_MATCH_SELECTION,
  CREATE_MATCH_EXERCISE_START,
  CREATE_MATCH_EXERCISE_FULFILLED,
  CREATE_MATCH_EXERCISE_REJECTED,
  CONFIRM_IMPORT_START,
  CONFIRM_IMPORT_FULFILLED,
  CONFIRM_IMPORT_REJECTED,
  FETCH_IMPORT_QUOTA_START,
  FETCH_IMPORT_QUOTA_REJECTED,
  SET_IMPORT_QUOTA,
  FETCH_PENDING_IMPORTS_START,
  FETCH_PENDING_IMPORTS_FULFILLED,
  FETCH_PENDING_IMPORTS_REJECTED,
  ABANDON_IMPORT_START,
  ABANDON_IMPORT_FULFILLED,
  ABANDON_IMPORT_REJECTED,
  LOAD_IMPORT_FOR_REVIEW_START,
  LOAD_IMPORT_FOR_REVIEW_FULFILLED,
  LOAD_IMPORT_FOR_REVIEW_REJECTED,
  SET_LAST_IMPORTED_PROGRAM_ID,
  CLEAR_LAST_IMPORTED_PROGRAM_ID,
  FETCH_AI_HEALTH_START,
  FETCH_AI_HEALTH_FULFILLED,
  FETCH_AI_HEALTH_REJECTED,
} from './actionTypes';

const initialState = {
  currentStep: IMPORT_STEPS.UPLOAD,
  file: null,
  isDiscovering: false,
  discoverError: null,
  sessionId: null,
  programs: [],
  selectedProgram: null,
  isExtracting: false,
  extractError: null,
  // True once the extract poll has waited past IMPORT_POLL_TIMEOUT_MS while the
  // backend job is still Processing. Switches StepProcessing from the spinner
  // to the "this is taking a while — check your drafts" message.
  isSlowImport: false,
  // Import_History id returned from step 2's 202 — required for polling and the
  // confirm call.
  importHistoryId: null,
  // Step-2 match list with staged selections for step 3.
  // Shape: { extractedName, extractedType, matchedExerciseId, matchedExerciseName,
  //          matchSource, selectedExerciseId, selectedExerciseName, isNew }
  matches: [],
  isConfirming: false,
  confirmError: null,
  createdPrograms: [],
  failedPrograms: [],
  isCreatingExercise: false,
  createExerciseError: null,
  // Backend-driven import quota (see node-server/.../aiImportQuotaService).
  // null until the first GET /saved-workouts/import/quota response or 403
  // QUOTA_EXCEEDED envelope hydrates it. Shape: ImportQuotaResponse —
  //   { tier, isTrial, planCapMonthly, usedThisMonth, creditsGranted,
  //     creditsRemaining, allowedRemaining, monthResetsAt, monthResetsAtDisplay,
  //     messaging: { headline, body, cta, upgradeUrl } }
  // The FE never derives allowedRemaining or the messaging strings — backend
  // is the source of truth so we don't drift from the plan rules.
  quota: null,
  isLoadingQuota: false,
  quotaError: null,
  // Pending Import_History rows surfaced in the Program navigator's
  // "Unfinished Imports" group. Hydrated by fetchPendingImports.
  pendingImports: [],
  isLoadingPending: false,
  pendingError: null,
  isAbandoning: false,
  abandonError: null,
  // Resume flow — true while GET /history/{id} is in flight.
  isLoadingHistoryDetail: false,
  loadHistoryError: null,
  // AI provider liveness (GET /healthcheck/ai-providers). null until the first
  // check resolves. Shape: { anthropic: { status, reason? }, openai: { status,
  //   reason? }, available: boolean, checkedAt }. We only block the upload step
  // when available === false; null (unknown / check errored) is treated as OK.
  aiHealth: null,
  isCheckingHealth: false,
  // Transient signal both post-import paths set to drive their success
  // feedback: the home-landing path (LD flag aiImportHomeLanding ON) renders
  // the Navigator's "…imported successfully" banner (cleared on program
  // change / 6s timer), and the build-landing path (flag OFF) renders the
  // Build page's AIImportSuccessModal (cleared when the coach clicks OK).
  // Always cleared on hard refresh.
  lastImportedProgramId: null,
};

const toMatchItem = (ex) => ({
  extractedName: ex.extractedName,
  extractedType: ex.extractedType,
  matchedExerciseId: ex.matchedExerciseId,
  matchedExerciseName: ex.matchedExerciseName,
  matchSource: ex.matchSource,
  // Backend-supplied confidence (0-100 numeric + human label). Null when the
  // backend couldn't match at all, or once the coach manually picks a
  // replacement (handled in UPDATE_MATCH_SELECTION below).
  matchConfidence: ex.matchConfidence == null ? null : ex.matchConfidence,
  matchConfidenceString: ex.matchConfidenceString || null,
  selectedExerciseId: ex.matchedExerciseId,
  selectedExerciseName: ex.matchedExerciseName,
  isNew: false,
});

function aiProgramImportReducer(state = initialState, action) {
  switch (action.type) {
    case SET_STEP:
      return { ...state, currentStep: action.payload };
    case SET_FILE:
      return { ...state, file: action.payload };
    case DISCOVER_PROGRAMS_START:
      return { ...state, isDiscovering: true, discoverError: null };
    case DISCOVER_PROGRAMS_FULFILLED:
      return {
        ...state,
        isDiscovering: false,
        sessionId: action.payload.sessionId,
        programs: action.payload.programs,
      };
    case DISCOVER_PROGRAMS_REJECTED:
      return { ...state, isDiscovering: false, discoverError: action.payload };
    case SET_SELECTED_PROGRAM:
      return { ...state, selectedProgram: action.payload };
    case EXTRACT_PROGRAM_START:
      return {
        ...state,
        isExtracting: true,
        extractError: null,
        isSlowImport: false,
        importHistoryId: null,
      };
    // 202 accepted — the background job is running. Keep isExtracting true so
    // StepProcessing stays mounted (it owns the poll loop keyed on
    // importHistoryId).
    case EXTRACT_PROGRAM_PROCESSING:
      return {
        ...state,
        importHistoryId: action.payload.importHistoryId,
      };
    case EXTRACT_PROGRAM_SLOW:
      return {
        ...state,
        isExtracting: false,
        isSlowImport: true,
      };
    case EXTRACT_PROGRAM_FULFILLED:
      return {
        ...state,
        isExtracting: false,
        isSlowImport: false,
        importHistoryId: action.payload.importHistoryId,
        matches: (action.payload.exercises || []).map(toMatchItem),
      };
    case EXTRACT_PROGRAM_REJECTED:
      return {
        ...state,
        isExtracting: false,
        isSlowImport: false,
        extractError: action.payload,
      };
    case UPDATE_MATCH_SELECTION:
      return {
        ...state,
        matches: state.matches.map((m) => (m.extractedName === action.payload.extractedName
          ? {
            ...m,
            selectedExerciseId: action.payload.exerciseId,
            selectedExerciseName: action.payload.exerciseName,
            isNew: action.payload.isNew || m.isNew,
          }
          : m)),
      };
    case CREATE_MATCH_EXERCISE_START:
      return { ...state, isCreatingExercise: true, createExerciseError: null };
    case CREATE_MATCH_EXERCISE_FULFILLED:
      return { ...state, isCreatingExercise: false };
    case CREATE_MATCH_EXERCISE_REJECTED:
      return { ...state, isCreatingExercise: false, createExerciseError: action.payload };
    case CONFIRM_IMPORT_START:
      return {
        ...state,
        isConfirming: true,
        confirmError: null,
      };
    case CONFIRM_IMPORT_FULFILLED:
      return {
        ...state,
        isConfirming: false,
        createdPrograms: action.payload.created,
        failedPrograms: action.payload.failed || [],
      };
    case CONFIRM_IMPORT_REJECTED:
      return {
        ...state,
        isConfirming: false,
        confirmError: action.payload,
      };
    case FETCH_IMPORT_QUOTA_START:
      return { ...state, isLoadingQuota: true, quotaError: null };
    case FETCH_IMPORT_QUOTA_REJECTED:
      return { ...state, isLoadingQuota: false, quotaError: action.payload };
    case SET_IMPORT_QUOTA:
      // Hydrates from the GET endpoint, post-extract refresh, or the 403
      // QUOTA_EXCEEDED envelope — all carry the same ImportQuotaState shape.
      return { ...state, isLoadingQuota: false, quota: action.payload };
    case FETCH_PENDING_IMPORTS_START:
      return { ...state, isLoadingPending: true, pendingError: null };
    case FETCH_PENDING_IMPORTS_FULFILLED:
      return { ...state, isLoadingPending: false, pendingImports: action.payload };
    case FETCH_PENDING_IMPORTS_REJECTED:
      return { ...state, isLoadingPending: false, pendingError: action.payload };
    case ABANDON_IMPORT_START:
      return { ...state, isAbandoning: true, abandonError: null };
    case ABANDON_IMPORT_FULFILLED:
      return {
        ...state,
        isAbandoning: false,
        pendingImports: state.pendingImports.filter(
          (item) => String(item.id) !== String(action.payload.historyId),
        ),
      };
    case ABANDON_IMPORT_REJECTED:
      return { ...state, isAbandoning: false, abandonError: action.payload };
    case LOAD_IMPORT_FOR_REVIEW_START:
      return { ...state, isLoadingHistoryDetail: true, loadHistoryError: null };
    case LOAD_IMPORT_FOR_REVIEW_FULFILLED:
      return {
        ...state,
        isLoadingHistoryDetail: false,
        currentStep: IMPORT_STEPS.REVIEW,
        importHistoryId: action.payload.importHistoryId,
        selectedProgram: action.payload.programName,
        matches: (action.payload.exercises || []).map(toMatchItem),
      };
    case LOAD_IMPORT_FOR_REVIEW_REJECTED:
      return { ...state, isLoadingHistoryDetail: false, loadHistoryError: action.payload };
    case FETCH_AI_HEALTH_START:
      return { ...state, isCheckingHealth: true };
    case FETCH_AI_HEALTH_FULFILLED:
      return { ...state, isCheckingHealth: false, aiHealth: action.payload };
    case FETCH_AI_HEALTH_REJECTED:
      // Fail open — keep the previous aiHealth (or null) so an errored check
      // never blocks the import.
      return { ...state, isCheckingHealth: false };
    case SET_LAST_IMPORTED_PROGRAM_ID:
      return { ...state, lastImportedProgramId: action.payload };
    case CLEAR_LAST_IMPORTED_PROGRAM_ID:
      return { ...state, lastImportedProgramId: null };
    case RESET_IMPORT:
      // Preserve the quota slice across resets — when the coach hits "start
      // over" we don't want the pill to disappear and refetch. The same
      // values are still valid; only the wizard flow resets. Pending imports
      // are also preserved so the navigator's group stays populated when the
      // coach abandons mid-resume and lands back at upload.
      // lastImportedProgramId is preserved because the wizard layout's
      // hasCompletedImport effect fires RESET_IMPORT immediately after a
      // successful confirm — without this, the id would be wiped before
      // ProgramNavigator mounts on the new route and could read it.
      return {
        ...initialState,
        quota: state.quota,
        isLoadingQuota: state.isLoadingQuota,
        quotaError: state.quotaError,
        pendingImports: state.pendingImports,
        lastImportedProgramId: state.lastImportedProgramId,
        // Account-level liveness; same values stay valid across a wizard reset.
        aiHealth: state.aiHealth,
      };
    default:
      return state;
  }
}

export default aiProgramImportReducer;
